/**
 * KPI result formatting (Phase 41A).
 *
 * Pure helpers with no runtime imports. Times read in the org's time zone
 * (`orgTimezone` from GET /api/me, spec D5). When that is missing or is not a
 * zone Intl knows, they read in the browser's zone, and a zone label always
 * names the zone the time was formatted in.
 */

import type {
  KpiStepStatus,
  KpiThresholds,
  KpiWhy,
  KpiWhyChangeover,
  KpiWhyPpe,
} from '@guild/shared';

// ============================================
// VALUES
// ============================================

/**
 * A KPI value in its unit: seconds (or `mm:ss`) as m:ss, percent to one
 * decimal, counts and whole numbers as integers, anything else to two
 * decimals. A missing value reads "--".
 */
export function formatKpiValue(
  value: number | null | undefined,
  unit: string | null | undefined,
  displayFormat: string | null | undefined
): string {
  if (value === null || value === undefined) return '--';

  if (unit === 'seconds' || displayFormat === 'mm:ss') {
    // Round the total first: 119.6 s reads 2:00, not 1:60
    const totalSeconds = Math.round(value);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  if (unit === 'percent' || unit === '%') {
    return `${value.toFixed(1)}%`;
  }

  if (unit === 'count' || Number.isInteger(value)) {
    return Math.round(value).toLocaleString();
  }

  return value.toFixed(2);
}

/**
 * The threshold legend for a KPI, mirroring `evaluateThresholds`
 * (apps/api/src/services/kpi-analysis.service.ts), which sets each saved
 * result's status:
 * - lower is worse when `target > (warning ?? 0)`: critical at or below the
 *   critical value, else warning at or below the warning value;
 * - otherwise higher is worse: critical at or above it, else warning at or
 *   above it.
 * Critical is tested first, so a warning value that is not on the normal side
 * of the critical one can never apply, and the legend leaves it out.
 */
export function describeThresholds(
  thresholds: KpiThresholds | undefined,
  unit: string | null | undefined,
  displayFormat: string | null | undefined
): string {
  const note = "Status comes from the KPI's value. Not-observed results have no status.";
  const warning = thresholds?.warning;
  const critical = thresholds?.critical;
  if (warning === undefined && critical === undefined) {
    return `This KPI has no thresholds — results are not flagged. ${note}`;
  }

  const target = thresholds?.target;
  const lowerIsWorse = !!target && target > (warning ?? 0);
  const worse = lowerIsWorse ? '≤' : '≥';
  const better = lowerIsWorse ? '>' : '<';
  const warningApplies =
    warning !== undefined &&
    (critical === undefined || (lowerIsWorse ? warning > critical : warning < critical));
  const format = (value: number) => formatKpiValue(value, unit, displayFormat);

  const bands: string[] = [];
  const normalBound = warningApplies ? warning : critical;
  if (normalBound !== undefined) bands.push(`Normal ${better} ${format(normalBound)}`);
  if (warningApplies) bands.push(`Warning ${worse} ${format(warning)}`);
  if (critical !== undefined) bands.push(`Critical ${worse} ${format(critical)}`);
  return `${bands.join(' · ')}. ${note}`;
}

/**
 * The row label for a saved result with no value (spec D3): NOT_OBSERVED →
 * "Not observed", NOT_ASSESSABLE → "Can't assess", anything else, a missing
 * status included → "No value".
 */
export function noValueLabel(observationStatus: unknown): string {
  if (observationStatus === 'NOT_OBSERVED') return 'Not observed';
  if (observationStatus === 'NOT_ASSESSABLE') return "Can't assess";
  return 'No value';
}

/**
 * A result's stored observation status (spec D12). The current API sends
 * `observationStatus`; the previous one sends `measurementData` instead, whose
 * `observationStatus` is read as the fallback (spec S16). Anything that is not
 * a string reads as null.
 */
export function rowObservationStatus(row: {
  observationStatus?: unknown;
  measurementData?: unknown;
}): string | null {
  const status =
    row.observationStatus ??
    (isRecord(row.measurementData) ? row.measurementData.observationStatus : undefined) ??
    null;
  return typeof status === 'string' ? status : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ============================================
// WHY (spec §4.2.1)
// ============================================
// The API builds `why` from a result's allowlisted structured fields,
// declared codes and one free-text field (a DEVIATION step's note)
// (apps/api/src/lib/kpi-result-view.ts), already sorted
// and capped. These helpers only word it: they never re-sort, and they read
// nothing but `why`.

/** Warning or critical. */
export function isFlagged(thresholdStatus: string | null | undefined): boolean {
  return thresholdStatus === 'warning' || thresholdStatus === 'critical';
}

/**
 * True when there is a Why panel to show: shape 'ppe' or 'changeover'. False
 * for 'none', for a missing `why` (the previous API, spec S16) and for a shape
 * this web does not know.
 */
export function hasWhyPanel(why: KpiWhy | null | undefined): why is KpiWhyPpe | KpiWhyChangeover {
  return why?.shape === 'ppe' || why?.shape === 'changeover';
}

/**
 * Whole seconds from the start of the clip as m:ss, e.g. "4:12". Minutes run
 * past 59 ("75:30"), matching the panel's "minutes:seconds" footer. Null for a
 * missing, negative or non-finite value.
 */
export function formatClipOffset(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

/** A step's span, "4:12–5:01"; one known end reads "4:12" or "until 5:01". */
export function formatClipRange(
  start: number | null | undefined,
  end: number | null | undefined
): string | null {
  const from = formatClipOffset(start);
  const to = formatClipOffset(end);
  if (from !== null && to !== null) return from === to ? from : `${from}–${to}`;
  if (from !== null) return from;
  return to === null ? null : `until ${to}`;
}

/**
 * A declared code in words: "dirty_registration_sensor" → "Dirty registration
 * sensor". Underscores and hyphens become spaces, and only the first letter's
 * case changes.
 */
export function humanizeCode(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A rule as shown: its code, followed by its label when it has one ("R3 Shirt");
 * null when no declared rule was recorded (the API then sends no label either).
 */
export function whyRuleName(rule: { rule: string | null; label: string | null }): string | null {
  if (rule.rule === null) return rule.label;
  return rule.label ? `${rule.rule} ${rule.label}` : rule.rule;
}

/**
 * A step as shown: its code, followed by its label when it has one ("2.3 Filter
 * basket"). A step whose code was not a declared one reads "Step {position}"
 * (its 1-based place in the stored list), followed by its label when it has one.
 */
export function whyStepName(step: {
  position: number;
  code: string | null;
  label: string | null;
}): string {
  const code = step.code ?? `Step ${step.position}`;
  return step.label ? `${code} ${step.label}` : code;
}

/** An exception as shown: its label, else its type in words, else "Exception". */
export function whyExceptionName(exception: { type: string | null; label: string | null }): string {
  if (exception.label !== null) return exception.label;
  return (exception.type === null ? '' : humanizeCode(exception.type)) || 'Exception';
}

/** "+3 more" for items the API left out past its cap; null when none were. */
export function whyMoreText(omitted: number | null | undefined): string | null {
  return typeof omitted === 'number' && omitted > 0 ? `+${omitted} more` : null;
}

const STEP_STATUS_LABELS: Record<KpiStepStatus, string> = {
  DEVIATION: 'Deviation',
  SKIPPED: 'Skipped',
  DONE: 'Done',
  NOT_VISIBLE: 'Not visible',
};

/** "Deviation", "Skipped", "Done", "Not visible"; any other status in words. */
export function stepStatusLabel(status: string): string {
  // Not Object.hasOwn: the build targets Vite 6's defaults (chrome87, safari14),
  // which predate it, and Vite does not polyfill
  return Object.prototype.hasOwnProperty.call(STEP_STATUS_LABELS, status)
    ? STEP_STATUS_LABELS[status as KpiStepStatus]
    : humanizeCode(status.toLowerCase());
}

/** A listed PPE violation: "4:12 · R3 · low". Missing parts are left out. */
export function whyViolationText(violation: KpiWhyPpe['violations'][number]): string {
  const parts = [formatClipOffset(violation.atSeconds), whyRuleName(violation), violation.severity];
  return parts.filter((part): part is string => !!part).join(' · ') || 'Violation';
}

/** A changeover step: "2.3 — Deviation — 4:12–5:01". */
export function whyStepText(step: KpiWhyChangeover['steps'][number]): string {
  const parts = [
    whyStepName(step),
    stepStatusLabel(step.status),
    formatClipRange(step.startSeconds, step.endSeconds),
  ];
  return parts.filter((part): part is string => !!part).join(' — ');
}

/** A changeover exception: "Dirty registration sensor — handled correctly — 7:45". */
export function whyExceptionText(exception: KpiWhyChangeover['exceptions'][number]): string {
  const handled =
    exception.handledCorrectly === null
      ? null
      : exception.handledCorrectly
        ? 'handled correctly'
        : 'not handled correctly';
  const parts = [whyExceptionName(exception), handled, formatClipOffset(exception.atSeconds)];
  return parts.filter((part): part is string => !!part).join(' — ');
}

/**
 * The panel's opening line: a bold count ("3 of 5"), its label and any
 * details, e.g. **3 of 5** assessable checks met · 1 not assessable · 2 visits.
 * Null when every count is missing.
 */
export function whySummary(why: KpiWhyPpe | KpiWhyChangeover): {
  lead: { count: string; label: string } | null;
  details: string[];
} | null {
  if (why.shape === 'changeover') {
    const { doneSteps, assessableSteps } = why;
    if (doneSteps === null || assessableSteps === null) return null;
    return {
      lead: { count: `${doneSteps} of ${assessableSteps}`, label: 'assessable steps done' },
      details: [],
    };
  }
  const { compliant, assessable, notAssessable } = why.checks;
  const lead =
    compliant !== null && assessable !== null
      ? { count: `${compliant} of ${assessable}`, label: 'assessable checks met' }
      : null;
  const details: string[] = [];
  if (notAssessable !== null) details.push(`${notAssessable} not assessable`);
  if (why.visitsObserved !== null) {
    details.push(`${why.visitsObserved} ${why.visitsObserved === 1 ? 'visit' : 'visits'}`);
  }
  return lead === null && details.length === 0 ? null : { lead, details };
}

/**
 * Whether a PPE panel adds "Every violation listed in this run is low
 * severity.": the result is warning or critical and lists at least one
 * violation, every one of them `low`. Read from the listed violations, never
 * from `worstSeverity`, which also covers violations past the cap.
 */
export function lowSeverityNoteApplies(
  why: KpiWhyPpe,
  thresholdStatus: string | null | undefined
): boolean {
  return (
    isFlagged(thresholdStatus) &&
    why.violations.length > 0 &&
    why.violations.every((violation) => violation.severity === 'low')
  );
}

/** True when the panel lists at least one clip time, which its footer then explains. */
export function whyListsTimes(why: KpiWhyPpe | KpiWhyChangeover): boolean {
  const shown = (seconds: number | null) => formatClipOffset(seconds) !== null;
  if (why.shape === 'ppe') return why.violations.some((violation) => shown(violation.atSeconds));
  return (
    why.steps.some((step) => shown(step.startSeconds) || shown(step.endSeconds)) ||
    why.exceptions.some((exception) => shown(exception.atSeconds))
  );
}

/**
 * The one-line descriptor shown with a result's status (G-21, spec §4.2.1):
 * - PPE: "<worst severity> · <rules with a violation>", e.g. "low · R3", or
 *   "no violation listed" for a flagged result that lists none;
 * - changeover: "2 of 5 steps done";
 * - null otherwise, and always for shape 'none' or a missing `why`.
 */
export function whyHeadline(
  why: KpiWhy | null | undefined,
  thresholdStatus: string | null | undefined
): string | null {
  if (!hasWhyPanel(why)) return null;
  if (why.shape === 'changeover') {
    const { doneSteps, assessableSteps } = why;
    // 0 assessable steps (e.g. a NOT_OBSERVED clip) has nothing to count
    return doneSteps !== null && assessableSteps !== null && assessableSteps > 0
      ? `${doneSteps} of ${assessableSteps} steps done`
      : null;
  }

  const rules = rulesWithViolation(why).join(', ');
  const parts = [why.worstSeverity, rules].filter((part): part is string => !!part);
  if (parts.length > 0) return parts.join(' · ');
  const listed = why.violations.length + why.omittedViolations;
  if (listed > 0) return `${listed} ${listed === 1 ? 'violation' : 'violations'} listed`;
  return isFlagged(thresholdStatus) ? 'no violation listed' : null;
}

/**
 * The rules with a violation, each once: per-rule rows with a violation count
 * first (in their order), then any other rule a listed violation names.
 */
function rulesWithViolation(why: KpiWhyPpe): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const add = (rule: { rule: string | null; label: string | null }) => {
    const name = whyRuleName(rule);
    if (rule.rule === null || name === null || seen.has(rule.rule)) return;
    seen.add(rule.rule);
    names.push(name);
  };
  why.rules.filter((rule) => (rule.violations ?? 0) > 0).forEach(add);
  why.violations.forEach(add);
  return names;
}

// ============================================
// RUNS (spec §4.4.1, S16, S18)
// ============================================
// A KPI run's state and reason code come from the API
// (apps/api/src/lib/kpi-run-state.ts). Both are read with a `switch`, so any
// value this web does not know, a prototype key such as 'constructor'
// included, reaches a default: a newer API may send states or reasons this web
// predates (S16).

/** A run badge's colour (spec S18). */
export type KpiRunBadgeTone = 'green' | 'blue' | 'red' | 'amber' | 'grey';

/**
 * The header badge of a completed KPI-only trigger, from its KPI run's state
 * (spec S18): "Completed", "Processing result", "Failed" (failed or never
 * queued), "Low confidence", "Not run" (skipped), and "No result" for
 * no_result, unknown_pre_tracking and any state this web does not know.
 */
export function kpiRunBadge(state: string | null | undefined): {
  label: string;
  tone: KpiRunBadgeTone;
} {
  switch (state) {
    case 'saved':
      return { label: 'Completed', tone: 'green' };
    case 'running':
      return { label: 'Processing result', tone: 'blue' };
    case 'failed':
    case 'enqueue_failed':
      return { label: 'Failed', tone: 'red' };
    case 'low_confidence':
      return { label: 'Low confidence', tone: 'amber' };
    case 'skipped':
      return { label: 'Not run', tone: 'grey' };
    case 'no_result':
    case 'unknown_pre_tracking':
    default:
      return { label: 'No result', tone: 'grey' };
  }
}

/**
 * What a KPI run without a result shows (spec §4.4.1), e.g. "Failed: the
 * analysis job failed." or "Low confidence (22%) — no result saved.". A saved
 * run shows its result instead; one whose result is missing reads like
 * no_result: "No KPI result was recorded for this run.", as does any state this
 * web does not know. A reason this web does not know reads "Failed." or
 * "Not run.".
 */
export function kpiRunMessage(run: {
  state: string;
  reasonCode?: string | null;
  confidence?: number | null;
}): string {
  switch (run.state) {
    case 'running':
      return 'Result still processing. This page checks again every 10 seconds.';
    case 'low_confidence': {
      const percent = confidencePercent(run.confidence);
      return percent === null
        ? 'Low confidence — no result saved.'
        : `Low confidence (${percent}%) — no result saved.`;
    }
    case 'failed':
      return failedRunMessage(run.reasonCode);
    case 'enqueue_failed':
      return 'Failed: the analysis was never queued.';
    case 'skipped':
      return skippedRunMessage(run.reasonCode);
    case 'unknown_pre_tracking':
      return "No result recorded. This run finished before run tracking began, so the reason wasn't kept.";
    default:
      return 'No KPI result was recorded for this run.';
  }
}

/** A failed run's wording, by reason code (spec §4.4.2). */
function failedRunMessage(reasonCode: string | null | undefined): string {
  switch (reasonCode) {
    case 'no_usable_answer':
      return 'Failed: the AI returned no usable answer.';
    case 'model_error':
      return 'Failed: the AI service returned an error.';
    case 'job_failed':
      return 'Failed: the analysis job failed.';
    case 'error':
      return 'Failed: an unexpected error.';
    default:
      return 'Failed.';
  }
}

/** A skipped run's wording, by reason code (spec §4.4.2). */
function skippedRunMessage(reasonCode: string | null | undefined): string {
  switch (reasonCode) {
    case 'out_of_scope':
      return "Not run: this camera is outside the KPI's scope.";
    case 'kpi_inactive':
      return 'Not run: the KPI was turned off.';
    case 'no_org_config':
      return "Not run: the KPI isn't configured for this organization.";
    case 'code_exec_disabled':
      return 'Not run: counting KPIs are switched off.';
    default:
      return 'Not run.';
  }
}

/**
 * A 0–1 confidence as a whole percent (0.22 → 22), rounded as the KPI Analytics
 * table rounds it; null when missing, not finite or outside 0–1.
 */
function confidencePercent(confidence: number | null | undefined): number | null {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) return null;
  if (confidence < 0 || confidence > 1) return null;
  return Math.round(confidence * 100);
}

// ============================================
// TIMES
// ============================================

/** "Oct 14, 9:12 AM" (add `timeZoneName: 'short'` for "Oct 14, 9:12 AM CDT"). */
export const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

/** "2:45 PM" */
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/** The local calendar date, to compare the dates of two instants. */
const LOCAL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

/**
 * `value` formatted in `timeZone`, or in the browser's zone when `timeZone` is
 * missing or not a zone Intl knows. An unparseable value reads "--".
 */
export function formatInTimeZone(
  value: Date | string | number,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return zonedFormat(timeZone, options).format(date);
}

/**
 * A trigger's clip window, e.g. "Oct 13, 2:45 PM – 3:00 PM CDT". The end shows
 * its own date when it falls on another local date, and the start names its
 * zone too when a DST change falls inside the window. Null when either end is
 * missing or unparseable, as for a result with no trigger.
 */
export function formatFootageWindow(
  start: string | null | undefined,
  end: string | null | undefined,
  timeZone: string | null | undefined
): string | null {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

  const sameDate =
    formatInTimeZone(startDate, timeZone, LOCAL_DATE_OPTIONS) ===
    formatInTimeZone(endDate, timeZone, LOCAL_DATE_OPTIONS);
  const zoneChanges = zoneName(startDate, timeZone) !== zoneName(endDate, timeZone);

  const startText = formatInTimeZone(
    startDate,
    timeZone,
    zoneChanges ? { ...DATE_TIME_OPTIONS, timeZoneName: 'short' } : DATE_TIME_OPTIONS
  );
  const endText = formatInTimeZone(endDate, timeZone, {
    ...(sameDate ? TIME_OPTIONS : DATE_TIME_OPTIONS),
    timeZoneName: 'short',
  });
  return `${startText} – ${endText}`;
}

/**
 * An org-local day or week key from GET /kpi-measurements/analytics, as
 * "Oct 15" or "Week of Oct 12". Only the first 10 characters are read, so the
 * previous API's 'YYYY-MM-DD 00:00:00' keys work too. The date is built with
 * Date.UTC and formatted in UTC, so the browser's zone cannot move it to
 * another day. A key that is not a date is returned unchanged.
 */
export function formatDayKey(key: string | null | undefined, style: 'day' | 'week'): string {
  const match = typeof key === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.slice(0, 10)) : null;
  if (!match) return key ?? '';
  const [, year, month, day] = match;
  const label = formatInTimeZone(
    new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
    'UTC',
    { month: 'short', day: 'numeric' }
  );
  return style === 'week' ? `Week of ${label}` : label;
}

/** An en-US formatter in `timeZone`; the browser's zone when that is missing or unknown. */
function zonedFormat(
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat('en-US', { ...options, timeZone });
    } catch {
      // RangeError: not a time zone Intl knows. Fall through to the browser's zone.
    }
  }
  return new Intl.DateTimeFormat('en-US', options);
}

/** The short zone name in force at `date`, e.g. "CDT". */
function zoneName(date: Date, timeZone: string | null | undefined): string {
  return (
    zonedFormat(timeZone, { timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}
